/**
 * Kas Store — Contant geld beheer per chauffeur per dag
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// Types

export interface Transactie {
  id: string;
  bedrag: number;
  betaalMethode: string;
  medewerkerIdSf: string;
  medewerkerNaam: string;
  datumTijdOntvangen: string;
  gecontroleerd: boolean;
  werkOrderId: string | null;
  werkOrderNaam: string | null;
  opmerkingen: string | null;
}

export interface ChauffeurKas {
  medewerkerIdSf: string;
  naam: string;
  transacties: Transactie[];
  totaalBedrag: number;
  aantalTransacties: number;
  alleGecontroleerd: boolean;
}

interface KasState {
  chauffeurs: ChauffeurKas[];
  selectedDate: string;
  isLoading: boolean;
  isUpdating: string | null; // medewerkerIdSf being updated
  totaalContant: number;
  totaalGecontroleerd: number;
  totaalOpen: number;
  openVorigeDagen: number;

  fetchTransacties: (date?: string) => Promise<void>;
  setDate: (date: string) => void;
  markeerGecontroleerd: (transactieIds: string[]) => Promise<void>;
}

// Helpers

function todayString(): string {
  const d = new Date();
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function officeFetch(path: string, method = 'GET', body?: unknown): Promise<unknown> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

function groupByChauffeur(transacties: Transactie[]): ChauffeurKas[] {
  const map = new Map<string, Transactie[]>();

  for (const t of transacties) {
    const key = t.medewerkerIdSf;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(t);
  }

  const result: ChauffeurKas[] = [];
  for (const [medewerkerIdSf, items] of map) {
    if (items.length === 0) continue;
    const totaal = items.reduce((sum, t) => sum + t.bedrag, 0);
    const alleGecontroleerd = items.every(t => t.gecontroleerd);
    result.push({
      medewerkerIdSf,
      naam: items[0]!.medewerkerNaam,
      transacties: items,
      totaalBedrag: totaal,
      aantalTransacties: items.length,
      alleGecontroleerd,
    });
  }

  // Sort: open first, then alphabetically
  result.sort((a, b) => {
    if (a.alleGecontroleerd !== b.alleGecontroleerd) {
      return a.alleGecontroleerd ? 1 : -1;
    }
    return a.naam.localeCompare(b.naam);
  });

  return result;
}

// Store

export const useKasStore = create<KasState>()((set, get) => ({
  chauffeurs: [],
  selectedDate: todayString(),
  isLoading: false,
  isUpdating: null,
  totaalContant: 0,
  totaalGecontroleerd: 0,
  totaalOpen: 0,
  openVorigeDagen: 0,

  setDate: (date: string) => {
    set({ selectedDate: date });
    get().fetchTransacties(date);
  },

  fetchTransacties: async (date?: string) => {
    const targetDate = date || get().selectedDate;
    set({ isLoading: true });

    try {
      // B5 (25-08): mirror-read via GET /api/transacties/kas (was twee
      // proxy-SOQL's). Het endpoint levert de Transactie-vorm 1-op-1.
      const data = await officeFetch(`/api/transacties/kas?date=${targetDate}`) as {
        data?: { transacties?: Transactie[]; openVorigeDagen?: number };
      };
      const transacties = data.data?.transacties ?? [];
      const chauffeurs = groupByChauffeur(transacties);

      const totaalContant = transacties.reduce((sum, t) => sum + t.bedrag, 0);
      const totaalGecontroleerd = transacties.filter(t => t.gecontroleerd).reduce((sum, t) => sum + t.bedrag, 0);
      const totaalOpen = totaalContant - totaalGecontroleerd;
      const openVorigeDagen = data.data?.openVorigeDagen ?? 0;

      set({
        chauffeurs,
        totaalContant,
        totaalGecontroleerd,
        totaalOpen,
        openVorigeDagen,
        isLoading: false,
      });
    } catch (error) {
      console.error('[Kas] fetchTransacties error:', error);
      set({ isLoading: false });
    }
  },

  markeerGecontroleerd: async (transactieIds: string[]) => {
    if (transactieIds.length === 0) return;

    // Find which chauffeur these belong to for loading state
    const chauffeurs = get().chauffeurs;
    const chauffeur = chauffeurs.find(c =>
      c.transacties.some(t => transactieIds.includes(t.id))
    );
    set({ isUpdating: chauffeur?.medewerkerIdSf || null });

    try {
      // B5 (25-08): eigen endpoint — patcht SF én seedt de mirror direct,
      // zodat de refetch hieronder de nieuwe stand ziet.
      await officeFetch('/api/transacties/markeer-gecontroleerd', 'POST', {
        transactieIds,
      });

      // Refresh data
      await get().fetchTransacties();
    } catch (error) {
      console.error('[Kas] markeerGecontroleerd error:', error);
    } finally {
      set({ isUpdating: null });
    }
  },
}));
